import {
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { CustomTypography } from "../CustomTypography/CustomTypography";
import useStyles from "./styles";
import { ReactNode } from "react";

type Props = {
  placeholder?: string;
  label?: string;
  options?: any[];
  marginBottom?: string | number;
  name?: string;
  value?: any;
  onChange?: (event: SelectChangeEvent<unknown>, child: ReactNode) => void;
  disabled?: boolean;
  /**
   * Human-readable text to show when `value` matches no option.
   *
   * This happens more often than it looks: the options are still loading, or
   * the stored id points at a row the current company's list does not
   * contain (a collaborator whose department was created under another
   * company, for instance). With no matching MenuItem the field surfaces the
   * raw scalar - a bare "8" where the user expects "Direction".
   *
   * Pass the label you already know from the API (e.g. `employee.department`,
   * which the employee endpoint returns alongside `department_id`). It is not
   * merely displayed: a matching MenuItem is synthesised from it, so the open
   * dropdown also marks the current value as selected instead of showing an
   * unexplained gap.
   */
  fallbackLabel?: string;
};

// CONTRACT: `value` is the SCALAR that matches an option's `value`
// (or the option string itself when options are plain strings) - never the
// whole option object.
//
// Passing an option object here used to render "[object Object]": MUI
// matches the selected value against each MenuItem's value, finds no
// match, and falls back to stringifying whatever it was given. That is a
// silent, purely visual failure, and it usually travels with a second one
// in the parent's onChange (reading `.value` off a scalar).
//
// Rather than fail that way, an object is unwrapped to its `value` and a
// warning is logged in development, so the next occurrence is obvious in
// the console instead of showing up in a screenshot from production.
const normalizeValue = (value: any) => {
  if (value && typeof value === "object" && "value" in value) {
    if (process.env.NODE_ENV === "development") {
      console.warn(
        "[CustomTextSelect] received an option object as `value`; " +
          "pass option.value instead. Check the parent's onChange too - " +
          "event.target.value is the scalar, not the option.",
        value
      );
    }
    return value.value ?? "";
  }
  return value ?? "";
};

const optionValue = (option: any) =>
  typeof option === "string" ? option : option?.value ?? option?.label ?? option;

const optionLabel = (option: any) =>
  typeof option === "string" ? option : option?.label;

/** An option carries a usable label when it is a plain string, or when its
 * `label` is a non-empty string. `{ value: 8 }` with no label does NOT count:
 * falling back to its value is precisely how an id reaches the screen. */
const hasUsableLabel = (option: any) => {
  const label = optionLabel(option);
  return typeof label === "string" && label.trim() !== "";
};

/** An identifier must never reach the user. A free-text value may legitimately
 * be displayed as-is (some selects are fed plain strings before their options
 * arrive), but a bare number is always an internal id leaking into the UI. */
const isIdLike = (value: any) =>
  typeof value === "number" ||
  (typeof value === "string" && /^\d+$/.test(value.trim()));

export const CustomTextSelect: React.FC<Props> = ({
  placeholder,
  label,
  options,
  marginBottom,
  name,
  value,
  disabled,
  fallbackLabel,
  onChange,
}) => {
  const classes = useStyles({ marginBottom });
  const resolved = normalizeValue(value);

  const providedOptions = options ?? [];
  const findOption = (list: any[], selected: any) =>
    list.find((option) => String(optionValue(option)) === String(selected));

  // Suppressing the raw id stopped it reaching the user, but it left the field
  // BLANK - the label still was not shown, which is the same bug seen from the
  // other side. When the stored id resolves to nothing in `options` and the
  // caller knows its label, reconstitute the missing entry instead: the Select
  // then has a real MenuItem to match, so it renders the label AND marks the
  // current service as selected in the open dropdown.
  //
  // Only kicks in when a fallbackLabel is supplied, so selects fed plain
  // strings keep their existing behaviour untouched.
  const isOrphanValue =
    resolved !== "" &&
    resolved !== null &&
    resolved !== undefined &&
    findOption(providedOptions, resolved) === undefined;

  const effectiveOptions =
    isOrphanValue && fallbackLabel
      ? [...providedOptions, { label: fallbackLabel, value: resolved }]
      : providedOptions;

  // Render the display text ourselves instead of relying on MUI's implicit
  // "find the matching MenuItem" behaviour, which falls back to the raw value
  // when nothing matches. That fallback is how a department id reached the
  // screen as "Service: 8" while the dropdown listed proper names.
  const renderSelected = (selected: any) => {
    // `displayEmpty` below means this also runs for the empty value, which is
    // how the `placeholder` prop finally reaches the screen: MUI's Select has
    // no native placeholder, so before this the field was simply blank until
    // the user picked something.
    if (selected === "" || selected === null || selected === undefined) {
      return placeholder ? (
        <span className={classes.placeholder}>{placeholder}</span>
      ) : (
        ""
      );
    }
    const match = findOption(effectiveOptions, selected);
    // A matched option is NOT enough: one without a usable label used to fall
    // through to String(optionValue(match)), i.e. the id again. Require the
    // label, and treat a labelless match like no match at all.
    if (match !== undefined && hasUsableLabel(match)) {
      return optionLabel(match);
    }
    if (process.env.NODE_ENV === "development") {
      console.warn(
        `[CustomTextSelect] value "${selected}" resolves to no labelled option` +
          (name ? ` (name="${name}")` : "") +
          ". Available: " +
          providedOptions.map((o) => optionValue(o)).join(", ") +
          ". Pass `fallbackLabel` so the field can show a name instead of a gap."
      );
    }
    if (fallbackLabel) return fallbackLabel;
    // Unresolvable id -> show nothing rather than an internal number.
    return isIdLike(selected) ? "" : String(selected);
  };

  return (
    <div className={classes.container}>
      {label && (
        <div className={classes.containerLabel}>
          <CustomTypography
            text={label}
            variant={"link"}
            textDecoration={"none"}
          />
        </div>
      )}
      <FormControl className={classes.formControl} variant="outlined">
        <Select
          name={name}
          label={placeholder}
          placeholder={placeholder}
          onChange={onChange}
          disabled={disabled}
          value={resolved}
          displayEmpty
          renderValue={renderSelected}
        >
          {effectiveOptions.map((option, index) => (
            <MenuItem key={index} value={optionValue(option)}>
              {optionLabel(option)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};
